import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getMetadata as getStorageMetadata,
  UploadMetadata,
} from 'firebase/storage';
import {
  IStorageAdapter,
  StorageFile,
  UploadOptions,
  DownloadOptions,
} from './storage-adapter.interface';

/**
 * Firebase Storage 어댑터
 * Firebase Storage를 사용한 파일 저장소 구현
 */
export class FirebaseStorageAdapter implements IStorageAdapter {
  private storage = getStorage();

  /**
   * 파일 업로드
   */
  async upload(
    file: File,
    path: string,
    options?: UploadOptions
  ): Promise<StorageFile> {
    const fileRef = ref(this.storage, path);

    // 메타데이터 설정
    const metadata: UploadMetadata = {
      contentType: options?.contentType || file.type,
      customMetadata: options?.metadata,
    };

    // 업로드 실행
    const snapshot = await uploadBytes(fileRef, file, metadata);

    // 다운로드 URL 가져오기
    const url = await getDownloadURL(snapshot.ref);

    return {
      path,
      url,
      size: snapshot.metadata.size,
      contentType: snapshot.metadata.contentType || file.type,
      createdAt: new Date(snapshot.metadata.timeCreated),
      metadata: snapshot.metadata.customMetadata,
    };
  }

  /**
   * 파일 다운로드 URL 가져오기
   */
  async getDownloadURL(
    path: string,
    options?: DownloadOptions
  ): Promise<string> {
    // 썸네일 경로 처리 (options.quality가 'thumbnail'인 경우)
    let targetPath = path;
    if (options?.quality === 'thumbnail') {
      // 'images' → 'thumbnails' 변환
      targetPath = path.replace('/images/', '/thumbnails/');
      targetPath = targetPath.replace(/(\.\w+)$/, '_thumb$1');
    }

    const fileRef = ref(this.storage, targetPath);
    return await getDownloadURL(fileRef);
  }

  /**
   * 파일 삭제
   */
  async delete(path: string): Promise<void> {
    const fileRef = ref(this.storage, path);
    await deleteObject(fileRef);
  }

  /**
   * 파일 존재 여부 확인
   */
  async exists(path: string): Promise<boolean> {
    try {
      const fileRef = ref(this.storage, path);
      await getStorageMetadata(fileRef);
      return true;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
        return false;
      }
      throw error;
    }
  }

  /**
   * 파일 메타데이터 조회
   */
  async getMetadata(path: string): Promise<StorageFile> {
    const fileRef = ref(this.storage, path);
    const metadata = await getStorageMetadata(fileRef);
    const url = await getDownloadURL(fileRef);

    return {
      path,
      url,
      size: metadata.size,
      contentType: metadata.contentType || 'application/octet-stream',
      createdAt: new Date(metadata.timeCreated),
      metadata: metadata.customMetadata,
    };
  }

  /**
   * URL에서 파일 경로 추출
   * 기존 storage.service.ts의 deleteFiles에서 사용하던 로직
   */
  extractPathFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)\?/);
      if (pathMatch) {
        return decodeURIComponent(pathMatch[1]);
      }
      return null;
    } catch (error) {
      console.error('URL 파싱 실패:', url, error);
      return null;
    }
  }

  /**
   * 여러 파일 삭제 (URL 기반)
   */
  async deleteMultiple(fileUrls: string[]): Promise<void> {
    const deletePromises = fileUrls.map(async (url) => {
      const path = this.extractPathFromUrl(url);
      if (path) {
        try {
          await this.delete(path);
        } catch (error) {
          console.error('파일 삭제 실패:', url, error);
        }
      }
    });

    await Promise.all(deletePromises);
  }
}

// 싱글톤 인스턴스 생성
export const firebaseStorageAdapter = new FirebaseStorageAdapter();
